/**
 * Compaction for the GOT_TEXAS / GOT_OMAHA payload queue (see App.tsx).
 *
 * PERF - table payloads are FULL per-table snapshots, so when several queue up
 * for the same table (which happens whenever the flush is deferred, e.g. while
 * a table-swipe gesture is in flight), only the NEWEST one needs dispatching.
 * Each dispatch costs a 26-71ms render task on a live table (measured; see
 * docs/investigations/20260819-game-client-overheating.md section 8k), so
 * flushing a deferred backlog un-compacted would replace mid-gesture stutter
 * with a post-landing freeze.
 *
 * Two fields must survive compaction rather than being dropped with the
 * superseded snapshot:
 *   - `tableHistory`: the rows a dropped payload carried are concatenated into
 *     the kept payload in arrival order. NOTE these rows are NOT incremental —
 *     the broadcaster re-derives them from the current game row on every push,
 *     so concatenating N snapshots of one unchanged `msg` yields N copies of
 *     the same row. Concatenating anyway is correct here (dropping rows would
 *     lose real events); the duplicates are collapsed on append, in
 *     `reducers/currentTable.tsx` `appendHistory`. Original update boundaries
 *     are retained so repeats in different hands remain distinct.
 *   - `game.chatMessages`: each payload may carry only the newest slice; the
 *     reducer merges by messageGUID, so concatenation is safe and lossless.
 */

import { getTableHistoryUpdates, TableHistoryUpdate } from './tableHistoryUpdates';

const asArray = (v: unknown): any[] => (Array.isArray(v) ? v : []);

/**
 * Collapse a payload queue to one entry per table (newest wins), preserving
 * every table's incremental history/chat rows. Payloads without a usable
 * gameID pass through untouched in their original position. Order of the
 * kept entries follows each table's LAST appearance, so cross-table ordering
 * of the newest data is preserved.
 */
export function compactTablePayloads(queue: any[]): any[] {
  if (queue.length < 2) return queue;

  const historyByGid = new Map<string, any[]>();
  const historyUpdatesByGid = new Map<string, TableHistoryUpdate[]>();
  const chatByGid = new Map<string, any[]>();

  const gidOf = (p: any): string | null => {
    const gid = String(p?.game?.gameID ?? '');
    return gid && gid !== '0' && gid !== 'undefined' ? gid : null;
  };

  // First pass: gather every payload's incremental rows in arrival order.
  for (const p of queue) {
    const gid = gidOf(p);
    if (!gid) continue;
    const hist = asArray(p?.tableHistory);
    if (hist.length) historyByGid.set(gid, [...(historyByGid.get(gid) ?? []), ...hist]);
    historyUpdatesByGid.set(gid, [
      ...(historyUpdatesByGid.get(gid) ?? []),
      ...getTableHistoryUpdates(p)
    ]);
    const chat = asArray(p?.game?.chatMessages);
    if (chat.length) chatByGid.set(gid, [...(chatByGid.get(gid) ?? []), ...chat]);
  }

  // Second pass: keep only each table's newest payload (by last appearance).
  const lastIndexByGid = new Map<string, number>();
  queue.forEach((p, i) => {
    const gid = gidOf(p);
    if (gid) lastIndexByGid.set(gid, i);
  });

  const out: any[] = [];
  queue.forEach((p, i) => {
    const gid = gidOf(p);
    if (!gid) {
      out.push(p);
      return;
    }
    if (lastIndexByGid.get(gid) !== i) return; // superseded by a newer snapshot
    const mergedHistory = historyByGid.get(gid);
    const mergedChat = chatByGid.get(gid);
    out.push({
      ...p,
      tableHistory: mergedHistory ?? asArray(p?.tableHistory),
      tableHistoryUpdates: historyUpdatesByGid.get(gid),
      game: mergedChat ? { ...p.game, chatMessages: mergedChat } : p.game
    });
  });
  return out;
}

/** How often a BACKGROUND (unfocused, un-gestured) table's state may render. */
export const BACKGROUND_TABLE_DISPATCH_MS = 2000;

/**
 * Holding cells for background tables' payloads (see App.tsx flush).
 *
 * PERF - the keep-alive carousel keeps every visited table MOUNTED, so each
 * dispatch to a background table renders its whole hidden subtree: measured as
 * the steady 55-105ms ambient main-thread tasks that both drop frames on the
 * ACTIVE table and jitter swipe gestures (investigation doc 8j/8q). Instead of
 * dispatching every message, a background table's payloads compact into one
 * held snapshot (newest wins; incremental history/chat rows concatenate - the
 * same rules as compactTablePayloads) and it renders at most once per
 * BACKGROUND_TABLE_DISPATCH_MS - or IMMEDIATELY when the player focuses it or
 * a gesture is about to reveal it. Nothing is ever lost and nothing on a
 * hidden felt is ever staler than 2s; the seat timers resync from their
 * tick-receipt stamps and snap-on-arrival already covers reveals.
 */
export class BackgroundTableBuffer {
  private held = new Map<string, any>();
  private lastDispatchAt = new Map<string, number>();

  /** Merge a payload into the table's held snapshot instead of dispatching. */
  hold(gid: string, payload: any): void {
    const prev = this.held.get(gid);
    this.held.set(gid, prev ? compactTablePayloads([prev, payload])[0] : payload);
  }

  /** True when the table is due a render (throttle window elapsed). */
  isDue(gid: string, nowMs: number): boolean {
    const last = this.lastDispatchAt.get(gid);
    return last === undefined || nowMs - last >= BACKGROUND_TABLE_DISPATCH_MS;
  }

  markDispatched(gid: string, nowMs: number): void {
    this.lastDispatchAt.set(gid, nowMs);
    this.held.delete(gid);
  }

  /** Take (and clear) the held snapshot for a table, if any. */
  take(gid: string): any | undefined {
    const p = this.held.get(gid);
    this.held.delete(gid);
    return p;
  }

  /** Gids currently holding a parked snapshot. */
  heldGids(): string[] {
    return Array.from(this.held.keys());
  }

  /** Drop everything for a table that is no longer open. */
  drop(gid: string): void {
    this.held.delete(gid);
    this.lastDispatchAt.delete(gid);
  }
}
